import { skipToken } from "@reduxjs/toolkit/query/react";
import { useAppSelector } from "app/store";
import { mainApi } from "services/main";

export interface UserOutput {
  alias: string;
  fullName: string;
  businessTitle: string;
  department: string;
}

export interface UserSearchRequest {
  teamName: string;
  keyword: string;
  limit?: number;
}

export interface UserSearchResponse {
  employees: UserOutput[];
}

export const LOOKUP_MAX_ALIASES = 100;
const LOOKUP_ALIAS_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/;

export interface EmployeeDetail {
  alias: string;
  fullName?: string;
  businessTitle?: string;
  department?: string;
}

export interface LookupEmployeesResponse {
  employees: Record<string, EmployeeDetail>;
  unresolved: { alias: string; reason: "NOT_FOUND" | "LOOKUP_FAILED" }[];
}

export const normalizeLookupAlias = (value?: string): string | undefined => {
  if (!value) return undefined;
  const trimmed = value.trim().toLowerCase();
  return LOOKUP_ALIAS_PATTERN.test(trimmed) ? trimmed : undefined;
};

export const normalizeLookupAliases = (
  values: (string | undefined)[],
): string[] =>
  [
    ...new Set(
      values
        .map(normalizeLookupAlias)
        .filter((v): v is string => v !== undefined),
    ),
  ].sort();

export const hasLookupFailed = (data?: LookupEmployeesResponse): boolean =>
  !!data?.unresolved.some((u) => u.reason === "LOOKUP_FAILED");

const extendedApi = mainApi.injectEndpoints({
  endpoints: (builder) => ({
    searchUsers: builder.query<UserSearchResponse, UserSearchRequest>({
      query: (request: UserSearchRequest) => ({
        url: `employees/${request.teamName}`,
        params: {
          keyword: request.keyword,
        },
      }),
      // Typeahead results are per-keystroke; keep them cached so repeat
      // keystrokes can reuse them instead of re-requesting.
      keepUnusedDataFor: 60 * 60,
    }),
    lookupEmployees: builder.query<
      LookupEmployeesResponse,
      { teamName: string; aliases: string[] }
    >({
      async queryFn({ teamName, aliases }, _api, _opts, baseQuery) {
        const merged: LookupEmployeesResponse = {
          employees: {},
          unresolved: [],
        };
        for (let i = 0; i < aliases.length; i += LOOKUP_MAX_ALIASES) {
          const chunk = aliases.slice(i, i + LOOKUP_MAX_ALIASES);
          const res = await baseQuery({
            url: `employees/${encodeURIComponent(teamName)}/lookup`,
            method: "POST",
            body: { aliases: chunk },
          });
          if (res.error) {
            merged.unresolved.push(
              ...chunk.map((alias) => ({
                alias,
                reason: "LOOKUP_FAILED" as const,
              })),
            );
            continue;
          }
          const data = res.data as LookupEmployeesResponse;
          Object.assign(merged.employees, data.employees);
          merged.unresolved.push(...data.unresolved);
        }
        return { data: merged };
      },
      keepUnusedDataFor: 24 * 60 * 60,
    }),
  }),
  overrideExisting: false,
});

export const {
  useSearchUsersQuery,
  useLazySearchUsersQuery,
  useLookupEmployeesQuery,
} = extendedApi;

export const userSearchApi = extendedApi;

const selectLookupEmployeesResult =
  extendedApi.endpoints.lookupEmployees.select;

export const useLookupEmployees = (
  arg: { teamName: string; aliases: string[] } | typeof skipToken,
) => {
  const realArg = arg === skipToken ? undefined : arg;

  const cached = useAppSelector(
    realArg ? selectLookupEmployeesResult(realArg) : () => undefined,
  ) as { data?: LookupEmployeesResponse } | undefined;

  return useLookupEmployeesQuery(arg, {
    refetchOnMountOrArgChange: hasLookupFailed(cached?.data) ? 60 : false,
  });
};
